import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  experimentPath,
  orgHomePath,
  orgSectionPath,
  taskAiTraitsPath,
  taskPath,
  trialPath,
  type OrgSection,
} from "@shared/appUrls";

/** Path builders for the org pages, bound to one organization. */
export interface OrgPaths {
  home(): string;
  section(name: OrgSection): string;
  trial(trialId: string): string;
  experiment(trialId: string, experimentId: string): string;
  task(trialId: string, experimentId: string, taskId: string): string;
  taskAiTraits(trialId: string, experimentId: string, taskId: string): string;
}

/**
 * With no organization selected every link is `/`, an old link that App sends
 * on to the current org's home, rather than an `/orgs//...` URL.
 */
const toRoot = () => "/";
const noOrgPaths: OrgPaths = {
  home: toRoot,
  section: toRoot,
  trial: toRoot,
  experiment: toRoot,
  task: toRoot,
  taskAiTraits: toRoot,
};

function orgPaths(orgId: string): OrgPaths {
  return {
    home: () => orgHomePath(orgId),
    section: (name) => orgSectionPath(orgId, name),
    trial: (t) => trialPath(orgId, t),
    experiment: (t, e) => experimentPath(orgId, t, e),
    task: (t, e, k) => taskPath(orgId, t, e, k),
    taskAiTraits: (t, e, k) => taskAiTraitsPath(orgId, t, e, k),
  };
}

/**
 * In-app links and navigations for the selected organization's pages
 * (`/orgs/{orgId}/...`). Stable until the selected org's id changes.
 */
export function useOrgPaths(): OrgPaths {
  const orgId = useAuth().selectedOrganization?.id;
  return useMemo(() => (orgId ? orgPaths(orgId) : noOrgPaths), [orgId]);
}
